import {Stack} from '@commercial-helios/ui/src/components/stack'
import {tokens, colors, radii} from '@commercial-helios/ui/theme'
import * as ListBox from '@reach/listbox'
import type {PropsWithChildren, ReactNode} from 'react'
import React from 'react'
import styled, {css} from 'styled-components'
import {v4 as uuidv4} from 'uuid'

import useKeyPress from '@helios/shared/hooks/use-key-press'
import type {PopoverDirection} from '@helios/shared/hooks/use-popover-direction'
import usePopoverDirection from '@helios/shared/hooks/use-popover-direction'
import navigateDownIcon from '@helios/shared/images/icons/navigate-down.svg'
import type {InputStylesProps} from '@helios/shared/styles/inputs'
import {inputStyles, Label} from '@helios/shared/styles/inputs'
import {Text, text} from '@helios/shared/styles/typography'
import {focusStyles} from '@helios/shared/styles/utils'

type ListboxListProps = {
    children: ReactNode
} & ListBox.ListboxListProps

export const ListboxList = (props: ListboxListProps) => {
    const {selectedOptionRef, isExpanded, highlightedOptionRef} = ListBox.useListboxContext()

    // Ensure that the selected item is in view when opening the popover
    React.useEffect(() => {
        setTimeout(() => {
            if (selectedOptionRef.current && isExpanded) {
                selectedOptionRef.current.scrollIntoView({block: 'nearest'})
            }
        }, 0)
    }, [isExpanded, selectedOptionRef])

    // Ensure that the highlighted item is in view at all times when using keyboard
    useKeyPress(null, () => {
        if (highlightedOptionRef.current) {
            highlightedOptionRef.current.scrollIntoView({block: 'nearest'})
        }
    })

    return <ListBox.ListboxList {...props} />
}

type Option<TValue extends string> = {
    value: TValue | ''
    label: string | JSX.Element
}

export interface Props<TValue extends string> {
    onChange: (newValue: TValue) => void
    options: Option<TValue>[]
    value: string
    label?: string
    placeholder: string
    error?: string | null
    id?: string
    required?: boolean
    'data-testid'?: string
}

const Select = <TValue extends string>({
    onChange,
    options,
    value,
    label,
    placeholder,
    error,
    required,
    'data-testid': dataTestId,
    ...props
}: PropsWithChildren<Props<TValue>>) => {
    const labelId = uuidv4()
    const triggerRef = React.useRef(null)
    const popoverRef = React.useRef(null)
    const direction = usePopoverDirection({triggerRef, popoverRef})

    return (
        <Wrapper space={8} stretch>
            {label && (
                <Label id={labelId} $required={required}>
                    {label}
                </Label>
            )}

            <StyledListboxInput
                aria-labelledby={labelId}
                value={value}
                onChange={onChange}
                aria-required={required}
                data-testId={dataTestId}
            >
                <div ref={triggerRef}>
                    <StyledListboxButton $direction={direction} $hasError={!!error}>
                        {(props) => (
                            <>
                                <SelectedOption $isPlaceholder={props.value === ''}>
                                    {options.find((option) => option.value === props.value)
                                        ?.label || <span>{placeholder}</span>}
                                </SelectedOption>

                                <Arrow $isExpanded={props.isExpanded} />
                            </>
                        )}
                    </StyledListboxButton>
                </div>

                <StyledListboxContainer>
                    <StyledListboxPopover ref={popoverRef} $direction={direction} portal={false}>
                        <ListboxList>
                            {options.map((option) => (
                                <StyledListboxOption
                                    key={option.value}
                                    value={option.value}
                                    {...(dataTestId && {
                                        'data-testid': `${dataTestId}-option-${option.value}`,
                                    })}
                                >
                                    {option.label}
                                </StyledListboxOption>
                            ))}
                        </ListboxList>
                    </StyledListboxPopover>
                </StyledListboxContainer>
            </StyledListboxInput>

            {error && (
                <Text variant="label-base" color="error" data-testid={`${dataTestId}-error`}>
                    {error}
                </Text>
            )}
        </Wrapper>
    )
}

export const Wrapper = styled(Stack)`
    width: 100%;
`

interface SelectedOptionProps {
    $isPlaceholder?: boolean
}

export const SelectedOption = styled.span<SelectedOptionProps>`
    color: ${(props) =>
        props.$isPlaceholder ? colors.text.placeholder : colors.textInput.default.foreground};
`

export const StyledListboxInput = styled(ListBox.ListboxInput)`
    width: 100%;
`

// Required as fix for issue with `react-lock-focus` package used by reach/listbox
// See more here: https://github.com/reach/reach-ui/issues/808#issuecomment-890301351
const StyledListboxContainer = styled.div`
    position: relative;
    z-index: 1;
`

interface StyledListboxButtonProps extends InputStylesProps {
    $direction: PopoverDirection
}

export const StyledListboxButton = styled(ListBox.ListboxButton)<StyledListboxButtonProps>(
    ({$direction}) => {
        const popoverAboveStyles = css`
            border-top-color: transparent;
            border-top-left-radius: 0;
            border-top-right-radius: 0;
        `

        const popoverBelowStyles = css`
            border-bottom-color: transparent;
            border-bottom-right-radius: 0;
            border-bottom-left-radius: 0;
        `

        return css`
            ${inputStyles}

            display: inline-flex;
            align-items: center;
            justify-content: space-between;
            cursor: default;
            user-select: none;

            &[aria-expanded='true'] {
                ${focusStyles()}

                ${$direction === 'above' && popoverAboveStyles}
                ${$direction === 'below' && popoverBelowStyles}
            }
        `
    }
)

interface StyledListboxPopoverProps {
    $direction: PopoverDirection
}

export const StyledListboxPopover = styled(ListBox.ListboxPopover)<StyledListboxPopoverProps>(
    ({$direction}) => {
        const popoverAboveStyles = css`
            border-top-left-radius: ${radii.arc8};
            border-top-right-radius: ${radii.arc8};
        `

        const popoverBelowStyles = css`
            border-bottom-right-radius: ${radii.arc8};
            border-bottom-left-radius: ${radii.arc8};
        `

        return css`
            position: absolute;
            width: 100%;
            min-width: initial;
            max-height: min(250px, 50vh);
            overflow-y: auto;
            background: #fff;
            border: 1px solid ${colors.border.secondary};
            outline: none;

            ${$direction === 'above' && popoverAboveStyles}
            ${$direction === 'below' && popoverBelowStyles}

            :focus-within {
                ${focusStyles()}
            }

            [data-reach-listbox-list] {
                padding: 0;
                margin: 0;
                list-style: none;
            }

            [data-reach-listbox-list]:focus {
                outline: none;
                box-shadow: none;
            }
        `
    }
)

export const StyledListboxOption = styled(ListBox.ListboxOption)`
    &,
    &[data-current] {
        ${text({variant: 'paragraph-base', color: 'muted'})}
        padding: ${tokens.spacing12} ${tokens.spacing20};
        cursor: pointer;
    }

    &[aria-selected='true'] {
        color: ${colors.text.default};
        background: ${colors.background.muted};
    }
`

interface ArrowProps {
    $isExpanded: boolean
}

export const Arrow = styled.span<ArrowProps>`
    width: 12px;
    height: 12px;
    background-image: url(${navigateDownIcon});
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    transform: ${(props) => props.$isExpanded && 'rotate(-180deg)'};
`

export default Select
